import i18n from '@/i18n';
import { apiService, type ApiResponse } from '@/services/apiService';
import type {
  AttachmentLimits,
  SupportFormData,
  SupportPayment,
  SupportReplyResult,
  SupportTicketDetail,
  SupportTicketList,
} from '@/features/support/types/support';

export interface SupportCategory {
  id: string;
  name: string;
  display_name: string;
  description: string;
  estimated_response_time: string;
  priority?: number;
}

export interface SupportCategoriesResponse {
  success: boolean;
  message: string;
  data?: {
    categories: SupportCategory[];
  };
  error?: string;
  statusCode?: number;
}

export interface SupportTicketResponse {
  success: boolean;
  message: string;
  data?: {
    ticket: {
      id: string;
      ticketNumber: string;
      email: string;
      category: string;
      categoryDisplayName?: string;
      status: string;
      createdAt: string;
      estimatedResponseTime: string;
    };
    /** Signed link token so the player can reopen the conversation without logging in */
    accessToken?: string;
    remainingCapacity: number;
  };
  error?: string;
  statusCode?: number;
  errors?: Array<{
    field: string;
    message: string;
  }> | Record<string, string>;
}

/**
 * Why a ticket could not be loaded: 'login' = no session and no link (401), 'expired' = a genuine
 * email link older than 30 days (410), 'not_found' = someone else's ticket or a tampered link (404).
 */
export type TicketLoadFailureReason = 'login' | 'expired' | 'not_found' | 'error';

/** Result of loading one ticket; `reason` explains a failure so the page can react (log in vs not found). */
export type TicketLoadResult =
  | { ok: true; ticket: SupportTicketDetail }
  | { ok: false; reason: TicketLoadFailureReason; message: string };

const MESSAGE_MAX_LENGTH = 1000;
const MESSAGE_MIN_LENGTH = 10;

/** Player-facing copy in the current language (this is not a component, so use the shared i18n instance). */
const tr = (key: string, options?: Record<string, unknown>): string => i18n.t(`supportDesk.${key}`, options);

function toFailure<T>(error: any, fallback: string): ApiResponse<T> {
  return {
    success: false,
    message: error?.message || fallback,
    error: error?.error || error?.code || 'NETWORK_ERROR',
    statusCode: error?.statusCode,
    errors: error?.errors,
  };
}

/** Append the signed link token (?t=) when the player opened the ticket from an email. */
function withToken(path: string, token?: string | null): string {
  return token ? `${path}${path.includes('?') ? '&' : '?'}t=${encodeURIComponent(token)}` : path;
}

class SupportService {
  readonly messageMaxLength = MESSAGE_MAX_LENGTH;

  /**
   * Submits a new support ticket. Signed-in players are linked to the ticket and their
   * contact details and tier are taken from the account by the server.
   */
  async getAttachmentLimits(): Promise<AttachmentLimits | null> {
    try {
      const response = await apiService.get<AttachmentLimits>('/support/attachment-limits', false, true);
      return response.success && response.data ? response.data : null;
    } catch {
      return null;
    }
  }

  async uploadAttachment(imageBase64: string): Promise<{ id: string } | null> {
    try {
      const response = await apiService.post<{ id: string }>('/support/uploads', { image: imageBase64 }, apiService.isAuthenticated());
      return response.success && response.data?.id ? response.data : null;
    } catch (error: any) {
      throw error;
    }
  }

  async submitTicket(ticketData: SupportFormData): Promise<SupportTicketResponse> {
    try {
      const response = await apiService.post('/support/tickets', ticketData, apiService.isAuthenticated());
      return {
        success: response.success,
        message: response.message,
        data: response.data as SupportTicketResponse['data'],
        error: response.error,
        statusCode: response.statusCode,
        errors: response.errors,
      };
    } catch (error: any) {
      return toFailure(error, tr('errors.submitTicket')) as SupportTicketResponse;
    }
  }

  /** One ticket for its owner, or through the signed link from the player's email. */
  async loadTicket(ticketNumber: string, token?: string | null): Promise<TicketLoadResult> {
    const signedIn = apiService.isAuthenticated();
    try {
      const response = await apiService.get<SupportTicketDetail>(
        withToken(`/support/tickets/${encodeURIComponent(ticketNumber)}`, token),
        signedIn,
        true,
      );
      if (response.success && response.data) return { ok: true, ticket: response.data };
      return { ok: false, reason: 'error', message: response.message || tr('errors.loadTicket') };
    } catch (error: any) {
      // Expired email links come back as 410 (a 401 would make apiService refresh the session).
      if (error?.statusCode === 410 || (error?.code || error?.error) === 'LINK_EXPIRED') {
        return { ok: false, reason: 'expired', message: tr('errors.linkExpired') };
      }
      if (error?.statusCode === 401) {
        return { ok: false, reason: 'login', message: tr('errors.loginRequired') };
      }
      if (error?.statusCode === 404) {
        return { ok: false, reason: 'not_found', message: tr('errors.notFound') };
      }
      return { ok: false, reason: 'error', message: error?.message || tr('errors.loadTicket') };
    }
  }

  /** The signed-in player's tickets, newest activity first. */
  async getMyTickets(page = 1, limit = 20): Promise<ApiResponse<SupportTicketList>> {
    try {
      return await apiService.get<SupportTicketList>(`/support/tickets?page=${page}&limit=${limit}`, true, true);
    } catch (error: any) {
      return toFailure(error, tr('errors.loadTickets'));
    }
  }

  /** Recent payments of the signed-in player (for "Which payment is this about?"). */
  async getMyPayments(): Promise<SupportPayment[]> {
    try {
      const response = await apiService.get<{ payments: SupportPayment[] }>('/support/my-payments', true, true);
      return response.success && response.data?.payments ? response.data.payments : [];
    } catch {
      return [];
    }
  }

  /**
   * Player reply. Replying to a resolved ticket, or to one auto-closed for no response within
   * its reopen window, reopens it (`data.ticket.reopened`).
   */
  async replyTicket(
    ticketNumber: string,
    message: string,
    token?: string | null,
    attachmentIds?: string[],
  ): Promise<ApiResponse<SupportReplyResult>> {
    try {
      return await apiService.post<SupportReplyResult>(
        withToken(`/support/tickets/${encodeURIComponent(ticketNumber)}/reply`, token),
        { message, ...(attachmentIds && attachmentIds.length ? { attachmentIds } : {}) },
        apiService.isAuthenticated(),
      );
    } catch (error: any) {
      return toFailure(error, tr('errors.reply'));
    }
  }

  /** "Yes, close ticket" — the player confirms it is solved. */
  async confirmResolved(ticketNumber: string, token?: string | null): Promise<ApiResponse> {
    try {
      return await apiService.patch(
        withToken(`/support/tickets/${encodeURIComponent(ticketNumber)}/done`, token),
        {},
        apiService.isAuthenticated(),
      );
    } catch (error: any) {
      return toFailure(error, tr('errors.close'));
    }
  }

  /**
   * Fetches all available support categories from the backend
   */
  async getCategories(): Promise<SupportCategoriesResponse> {
    try {
      const response = await apiService.get('/support/categories', false);
      return {
        success: response.success,
        message: response.message,
        data: response.data as SupportCategoriesResponse['data'],
        error: response.error,
        statusCode: response.statusCode,
      };
    } catch (error: any) {
      return toFailure(error, tr('errors.categories')) as SupportCategoriesResponse;
    }
  }

  /**
   * Check remaining ticket capacity for an email (guests only; signed-in limits are enforced server-side)
   */
  async checkTicketCapacity(email: string): Promise<ApiResponse> {
    try {
      return await apiService.get(`/support/tickets/capacity?email=${encodeURIComponent(email)}`, false);
    } catch (error: any) {
      return toFailure(error, tr('errors.capacity'));
    }
  }

  /** @deprecated use loadTicket */
  async getTicketByNumber(ticketNumber: string, token?: string | null): Promise<ApiResponse> {
    const result = await this.loadTicket(ticketNumber, token);
    return result.ok
      ? { success: true, message: 'OK', data: result.ticket }
      : { success: false, message: result.message, error: result.reason };
  }

  /**
   * Validates support form data (messages in the current language).
   */
  validateSupportForm(data: SupportFormData): { isValid: boolean; errors: Record<string, string> } {
    const errors: Record<string, string> = {};

    if (!data.email.trim()) {
      errors.email = tr('validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) {
      errors.email = tr('validation.emailInvalid');
    } else if (data.email.trim().length > 254) {
      errors.email = tr('validation.emailTooLong');
    }

    if (!data.phoneNumber.trim()) {
      errors.phoneNumber = tr('validation.phoneRequired');
    } else {
      const trimmedPhone = data.phoneNumber.trim();
      const phoneLength = trimmedPhone.length;
      if (phoneLength < 10 || phoneLength > 20) {
        errors.phoneNumber = tr('validation.phoneLength');
      } else {
        const usFormatWithParentheses = /^(\+?1[-.\s]?)?\(([0-9]{3})\)\s?([0-9]{3})[-.]([0-9]{4})$/;
        const usFormatWithDashes = /^(\+?1[-.\s]?)?([0-9]{3})[-.]([0-9]{3})[-.]([0-9]{4})$/;
        const cleanedPhone = trimmedPhone.replace(/[\s\-().]/g, '');
        const internationalFormat = /^\+?[1-9]\d{9,14}$/;

        if (/\s-\s|\s-|-\s/.test(trimmedPhone)) {
          errors.phoneNumber = tr('validation.phoneInvalid');
        } else {
          const isValidFormat =
            usFormatWithParentheses.test(trimmedPhone) ||
            usFormatWithDashes.test(trimmedPhone) ||
            internationalFormat.test(cleanedPhone);
          if (!isValidFormat) {
            errors.phoneNumber = tr('validation.phoneInvalid');
          }
        }
      }
    }

    if (!data.category_id) {
      errors.category_id = tr('validation.topicRequired');
    }

    if (!data.message.trim()) {
      errors.message = tr('validation.messageRequired');
    } else if (data.message.trim().length < MESSAGE_MIN_LENGTH) {
      errors.message = tr('validation.messageTooShort', { min: MESSAGE_MIN_LENGTH });
    } else if (data.message.length > MESSAGE_MAX_LENGTH) {
      errors.message = tr('validation.messageTooLong', { max: MESSAGE_MAX_LENGTH });
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }

  /**
   * Formats phone number for display
   */
  formatPhoneNumber(phoneNumber: string): string {
    const cleaned = phoneNumber.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phoneNumber;
  }

  /**
   * Estimated first-reply time for a category id (falls back to 24 hours).
   */
  getEstimatedResponseTime(category: string, categories: SupportCategory[] = []): string {
    const fromApi = categories.find((c) => c.id === category)?.estimated_response_time;
    if (fromApi) return fromApi;
    const fallback: Record<string, string> = {
      withdrawal_issues: '1-2 hours',
      deposit_issues: '2-4 hours',
      payment_processing: '2-4 hours',
      kiosk_technical: '2-6 hours',
      password_reset: '2-6 hours',
      account_issues: '4-8 hours',
      email_verification: '4-12 hours',
    };
    return fallback[category] || '24-48 hours';
  }

  /**
   * Priority hint shown to the player for a category id.
   */
  getPriorityLevel(category: string): 'high' | 'medium' | 'low' {
    if (['deposit_issues', 'withdrawal_issues', 'payment_processing'].includes(category)) return 'high';
    if (['account_issues', 'kiosk_technical', 'password_reset', 'email_verification'].includes(category)) return 'medium';
    return 'low';
  }
}

export const supportService = new SupportService();
